/*
 * Copyright (c) 2025 NEXTWELL INTL SRL. All Rights Reserved.
 * This software is the proprietary information of NEXTWELL INTL SRL.
 * Use is subject to license and non-disclosure terms
 */

import { Directive, ElementRef, HostBinding, model, OnDestroy, OnInit } from '@angular/core';
import { debounceTime, fromEvent, merge, Subscription } from 'rxjs';

@Directive({
  selector: '[sharedUiSetOverlayPositionClasses]',
  standalone: true
})
export class SetOverlayPositionClassesDirective implements OnInit, OnDestroy {
  private readonly threshold = 300;
  private subs = new Subscription();
  isLeft = model(false);
  isRight = model(false);
  isTop = model(false);
  isBottom = model(false);
  @HostBinding('class.overlay-left') get isOverlayLeft() { return this.isLeft(); }
  @HostBinding('class.overlay-right') get isOverlayRight() { return this.isRight(); }
  @HostBinding('class.overlay-top') get isOverlayTop() { return this.isTop(); }
  @HostBinding('class.overlay-bottom') get isOverlayBottom() { return this.isBottom(); }

  constructor(
    private el: ElementRef<HTMLElement>,
  ) {}

  ngOnInit() {
    // run once immediately
    this.updatePosition();

    // throttle scroll & resize to animationFrame
    const scroll$ = fromEvent(window, 'scroll');
    const resize$ = fromEvent(window, 'resize');
    this.subs.add(
      merge(scroll$, resize$)
        .pipe( debounceTime(100))
        .subscribe(() => this.updatePosition())
    );
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  private get vw() { return window.innerWidth; }
  private get vh() { return window.innerHeight; }

  private get bounds() {
    const firstEl = this.el.nativeElement.firstElementChild;
    if(!firstEl ) {
      throw new Error('SetOverlayPositionClassesDirective: No child element found.');
    }
    return firstEl.getBoundingClientRect();
  }

  updatePosition() {
    const { top, bottom, left, right } = this.bounds;
    const l = Math.round(left);
    const r = Math.round(right);
    const t = Math.round(top);
    const b = Math.round(bottom);

    this.isRight.set( (this.vw - r) < this.threshold );
    this.isLeft.set( !this.isRight() );

    this.isBottom.set( (this.vh - b) < this.threshold );
    this.isTop.set( !this.isBottom() );

  }
}
